<template>
  <div class="treatments-and-date">
    <div
      v-if="shouldShowMastheadAndTitle"
      class="treatments-and-date__header"
    >
      <a
        class="treatments-and-date__back-cta"
        :href="`/${lang}${propertyData.detailsPageUrl}`"
      >
        {{ tc('spaSoft.step1.backToSpaCta') }}
      </a>
      <h2 class="treatments-and-date__title">
        {{ tc('spaSoft.step1.guestTimeDateSelection.title') }}
      </h2>
    </div>
    <div
      v-if="itineraryErrors"
      class="treatments-and-date__error-banner"
      role="alert"
    >
      <span
        class="icon-warning-red"
        aria-hidden="true"
      />
      <ul class="treatments-and-date__error-banner-list">
        <li
          v-for="(error, index) in itineraryErrorsMessages"
          :key="index"
          v-html="error.errorMessage"
        />
      </ul>
    </div>
    <SpaBookingDetailsForm />
    <hr class="spacer">
    <GuestTreatmentSelector
      v-for="(guest, key) in guestsSelectedData"
      :key="key"
      :guest-data="guest"
      :guest-key="Number(key)"
    />
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import SpaBookingDetailsForm from './components/spa-booking-details-form.vue'
  import GuestTreatmentSelector from './components/guest-treatment-selector.vue'

  export default {
    name: 'TreatmentsAndDate',
    components: {
      SpaBookingDetailsForm,
      GuestTreatmentSelector,
    },
    mixins: [Tracking],
    beforeRouteEnter(to, from, next) {
      next(async (vm) => {
        if (vm.ready) {
          await vm.updateFooterLabel()
          vm.selectItinerary(null)
          vm.cancelAvailableItineraries()
        }
        return true
      })
    },
    data() {
      return {
        firstTimeLoad: true
      }
    },
    computed: {
      ...mapState('global', ['lang']),
      ...mapState('spaSoft', ['ready', 'validCategories', 'itineraryErrors', 'hotelCode', 'propertyData', 'selectedDate', 'preferredStartTime']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('spaSoft', ['guestsSelectedData', 'numberOfGuests', 'shouldShowMastheadAndTitle']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),

      itineraryErrorsMessages() {
        if (this.itineraryErrors?.type === 'missingTreatments') {
          const errorMessages = []
          Object.keys(this.itineraryErrors.data).forEach((_, guestIndex) => {
            this.itineraryErrors.data[guestIndex].forEach((treatmentId) => {
              const guestData = this.guestsSelectedData[guestIndex]
              const treatment = guestData.selectedTreatments.find(
                (t) => t.selectedTimeSlot.spaSoftId === treatmentId
              )
              const treatmentName = treatment?.sitecoreTreatmentDetails?.title

              const errorMessage = this.tc('spaSoft.step1.itinerarySelection.itineraryNotAvailableErrorMessage', {
                treatmentName,
                guestNumber: Number(guestIndex) + 1,
              })
              errorMessages.push({
                errorMessage, treatment, guestNumber: Number(guestIndex) + 1, type: 'missingTreatments'
              })
            })
          })
          return errorMessages
        }
        if (this.itineraryErrors?.type === 'woops') {
          return [{
            errorMessage: this.itineraryErrors?.data?.message,
            treatment: null,
            guestNumber: null,
            type: 'woops'
          }]
        }
        return [{
          errorMessage: this.itineraryErrors?.data?.message || 'Internal Error',
          treatment: null,
          guestNumber: null,
          type: 'unknown'
        }]
      },
      cartTotalPrice() {
        const calculateGuestTreatmentsTotal = (guestData) => (guestData.selectedTreatments || [])
          .reduce((acc, treatment) => {
            const price = treatment.selectedTimeSlot.apiTreatmentDetails.price.totalCalculated
            return acc + price
          }, 0)

        const totalPrice = Object.values(this.guestsSelectedData)
          .reduce((acc, guestData) => acc + calculateGuestTreatmentsTotal(guestData), 0)

        return this.getDisplayPrice({
          price: totalPrice,
          fromCurrency: this.propertyData?.currency?.isoCode,
          toCurrency: this.orSelected(this.propertyData?.currency?.isoCode),
          noSymbol: true
        })
      },
      hasAnyTreatments() {
        return Object.values(this.guestsSelectedData)
          .some((guest) => guest.selectedTreatments?.length > 0)
      }
    },
    watch: {
      async ready(val) {
        if (val) {
          await this.updateFooterLabel()
          this.selectItinerary(null)
          this.cancelAvailableItineraries()
        }
      },
      guestsSelectedData: {
        handler() {
          this.resetItineraryErrors()
        },
        deep: true
      },
      itineraryErrors: {
        handler(val) {
          if (val) {
            window.scrollTo(0, 0)
            this.trackSpaItineraryErrors()
          }
        },
        deep: true
      },
      hasAnyTreatments: {
        immediate: true,
        handler(hasAnyTreatments, oldValue) {
          if (hasAnyTreatments && (oldValue === false || this.firstTimeLoad)) {
            this.trackSpaViewCart()
            this.firstTimeLoad = false
          }
        }
      }
    },
    created() {
      this.$track({ event: 'pageView' })
    },
    methods: {
      ...mapActions('spaSoft', ['updateNextStepButton', 'getSpaAvailableCategories', 'resetItineraryErrors', 'selectItinerary', 'cancelAvailableItineraries', 'trackSpaViewCart']),
      async updateFooterLabel() {
        const label = this.tc('spaSoft.step1.guestTimeDateSelection.continueToSelectItineraryCta')
        await this.updateNextStepButton({
          label,
          step: 'validate',
        })
      },
      trackSpaItineraryErrors() {
        this.itineraryErrorsMessages.forEach((error) => {
          if (error.type === 'missingTreatments') {
            this.$track(this.getTrackingData('spaSoft', null, {
              event: 'spaCartError',
              hotelCode: this.hotelCode,
              stepNumber: 1.7,
              errorMessage: error?.errorMessage,
              products: [
                {
                  guestNumber: error?.guestNumber,
                  productType: 'Treatment',
                  treatmentName: error?.treatment?.sitecoreTreatmentDetails?.title,
                  treatmentType: error?.treatment?.treatmentCategory?.title,
                  duration: error?.treatment?.selectedTimeSlot?.duration,
                  price: this.getDisplayPrice({
                    price: error?.treatment?.selectedTimeSlot?.apiTreatmentDetails?.price?.totalCalculated,
                    fromCurrency: this.propertyData?.currency?.isoCode,
                    toCurrency: this.orSelected(this.propertyData?.currency?.isoCode),
                    noSymbol: true
                  }),
                  currency: this.orSelected(this.propertyData?.currency?.isoCode),
                  preferredTherapistGender: error?.treatment?.selectedTimeSlot?.preferredGender
                }
              ]
            }))
          }
        })
      }
    }
  }
</script>
